<template>
  <section id="works" class="common-padding mb-20">
    <div class="flex flex-col">
      <h3
        id="selectedWorks"
        v-html="selectedWorks"
        class="heading-1 text-start leading-none font-bold uppercase"
      ></h3>
      <p
        class="text-flax-smoke-400 text-opacity-50 heading-1 hidden w-4/5 text-end font-extrabold sm:block"
      >
        ( {{ selectedWorksProps.length }} )
      </p>

      <div
        id="selected-works-text"
        class="text-flax-smoke-300 md:column-gap mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="text-flax-smoke-300/85 heading-6 col-span-4 text-center text-nowrap lg:col-start-2"
        >
          (
          <span class="inline sm:hidden">{{ selectedWorksProps.length }} </span>
          PROJECTS )
        </p>
        <p
          class="font-fancy heading-4 col-span-8 w-full text-balance sm:font-semibold lg:col-span-7"
        >
          Featured client projects that have been meticulously crafted with
          passion and purpose over the years.
        </p>
      </div>
    </div>

    <div
      class="sm:column-gap relative mt-12 grid size-full grid-cols-12 lg:mt-[10%]"
    >
      <div
        class="text-flax-smoke-100 sticky top-12 col-span-5 hidden h-fit w-full overflow-hidden text-[22vw] leading-[0.8] font-semibold md:flex"
      >
        <span class="font-title! relative -tracking-wider">0</span>
        <span
          id="index"
          class="font-title! relative -tracking-wider will-change-transform"
          >{{ index + 1 }}.</span
        >
      </div>
      <aside
        @mouseenter="showCursor"
        @mouseleave="hideCursor"
        class="relative col-span-full flex flex-col space-y-20 md:col-span-7"
      >
        <div
          v-for="(work, i) in selectedWorksProps"
          :key="i"
          class="work-card @container"
        >
          <a class="group" target="_blank" :href="work.url">
            <div
              class="relative flex aspect-square items-center justify-center overflow-clip rounded-lg"
            >
              <img
                alt="work-background"
                loading="lazy"
                class="absolute size-full object-cover select-none"
                :src="work.imageBg"
              />
              <div
                class="aspect-[4/3]size-full z-10 flex items-center justify-center overflow-clip rounded-lg object-contain"
              >
                <!-- autoplay="false" -->
                <!-- <video
                  ref="videoRefs"
                  :src="work.videoSrc"
                  muted
                  :autoplay="false"
                  type="video/webm"
                  class="rounded-md size-[80%] object-contain transition-all duration-500 ease-in-out blur"
                ></video> -->
              </div>
            </div>
            <div>
              <div class="flex-col items-start sm:flex">
                <h3 class="font-title! heading-3 font-bold uppercase">
                  {{ work.name }}
                </h3>
                <div class="mb-[20px] flex flex-wrap gap-2 select-none">
                  <p
                    class="hover:bg-flax-smoke-300 border-flax-smoke-300 hover:text-flax-smoke-900 transition-background-color transition-colorduration-500 rounded-full border px-4 py-2 ease-in-out"
                    v-for="tag in work.tags"
                    :key="tag"
                  >
                    <span>{{ tag }}</span>
                  </p>
                  <p
                    v-if="work && work.year"
                    class="bg-flax-smoke-300 border-flax-smoke-300 text-flax-smoke-900 hover:text-flax-smoke-300 transition-background-color transition-colorduration-500 rounded-full border px-4 py-2 ease-in-out hover:bg-transparent"
                  >
                    <span>{{ work.year }}</span>
                  </p>
                </div>
              </div>
              <div class="description overview">
                <p>{{ work.overview }}</p>
              </div>
            </div>
          </a>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { computed, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue';
  import gsap from 'gsap';
  import { useWindowSize } from '@vueuse/core';
  import { work1, work2, work3, work4, work5 } from '@/assets/videos';
  import { workBg1, workBg2, workBg3, workBg4, workBg5 } from '@/assets/images';
  const videoRefs = useTemplateRef<HTMLVideoElement[]>('videoRefs');

  const isSmallScreen = computed(() => {
    return useWindowSize().width.value < 768;
  });
  const index = ref(0);
  const selectedWorks = ref('Selected Projects /');

  const tl = gsap
    .timeline({ defaults: { duration: 0.25 } })
    .to(['#cursor', '#inner'], {
      scale: 1,
      opacity: 1,
    })
    .paused(true);

  const showCursor = () => {
    tl.play();
  };
  const hideCursor = () => {
    tl.reverse();
  };

  const selectedWorksProps = [
    {
      name: 'South Western Sydney PHN',
      category: 'Front-end & Back-end',
      overview: `South Western Sydney PHN is a public health organization dedicated to improving health outcomes across the region. I worked as a Full-Stack WordPress Developer, focusing on building a fully custom WordPress theme tailored for a wide range of health-related content. My work included developing custom templates, structuring content with custom post types and ACF, and implementing a powerful custom search feature to improve content discovery across services, news, and resources.

The project also required responsive front-end development, accessibility enhancements, and optimized admin workflows to support a non-technical editorial team.`,
      //tags: ['Wordpress', 'CSS3', 'HTML5', 'jQuery', 'JS', 'ACF', 'LAMP'],
      tags: [],
      videoSrc: work5,
      imageBg: workBg5,
      url: 'https://swsphn.com.au/',
      year: '',
    },
    {
      name: 'Melthon Learning Directory',
      category: 'Back-end',
      overview: `Melton Learning Directory is a public-facing community education platform that promotes local learning programs, workshops, and events. I worked as a Full-Stack WordPress Developer, with a primary focus on customizing WooCommerce to support a unique registration and booking system tailored for community education needs. I extended WooCommerce to handle custom product types, user roles, and non-standard checkout flows.

On the front end, I supported responsive design integration, template overrides for WooCommerce pages, and dynamic content rendering. I also developed custom post types and admin workflows to ensure easy content management for non-technical staff.

`,
      // tags: ['Wordpress','Woocommerce', 'Custom B2B Plugin'],
      tags: [],
      videoSrc: work2,
      imageBg: workBg2,
      url: 'https://meltonlearning.vic.gov.au/',
      year: '',
    },
    {
      name: 'FIRG',
      category: 'Front-end & Back-end',
      overview: `FIRG is a member-based organization offering data and insights on remuneration, benefits, and HR trends in the financial services sector. As a Full-Stack Developer, I focused primarily on back-end development, building a custom WordPress plugin to handle secure REST API integrations for external systems and member access. I also supported front-end development to ensure seamless integration of dynamic data and interactive elements into the site’s responsive design.

This work included optimizing data flows, implementing custom post types, and enhancing the admin experience for easy content and member management.

`,
      // tags: ['Wordpress', 'Custom Plugin ', 'API integration','LAMP'],
      tags: [],
      videoSrc: work3,
      imageBg: workBg3,
      url: 'https://firg.com.au/',

      year: '',
    },
    {
      name: 'Garden Life E-commerce Website',
      category: 'Full-Stack Developer',
      overview:
        'Garden Life is a modern, responsive e-commerce website built on WordPress and WooCommerce. I served as the full-stack developer, with a primary focus on backend development. My contributions included building a custom WordPress plugin, integrating external APIs, and customizing WooCommerce functionality - while also supporting frontend layout and responsiveness.',
      // tags: ['Wordpress', 'Woocommerce','PHP','Javascript','REST API','LAMP','ACF','SCSS'],
      tags: [],
      videoSrc: work4,
      imageBg: workBg4,
      url: 'https://gardenlife.com.au/',
      year: '',
    },
    {
      name: 'Psynergy Equine',
      category: 'Front-end and Back-end ',
      overview:
        'I built a fully custom WordPress website for Psynergy Equine, a luxury Arabian horse breeding program. The site features a bespoke WordPress theme, responsive front-end design, and intuitive admin tools tailored to manage horses, lineages, and media. I developed an interactive pedigree visualization using D3.js to dynamically explore bloodlines. The overall layout emphasizes clean, editorial storytelling to reflect the brand’s prestige and heritage.',
      //  tags: ['Wordpress Custom Theme/Plugin','HTML5','SCSS','ACF','LAMP','D3.js','Slick.js','Tailwind','Responsive UI/UX Design'],
      tags: [],
      videoSrc: work1,
      imageBg: workBg1,
      url: 'https://psynergyequine.com/',
      year: '',
    },
  ];

  // Reusable function to handle forward scroll animation
  const createForwardTimeline = (
    index: any,
    i: any,
    selectedWorksProps: any[],
  ) => {
    const tl = gsap.timeline({
      defaults: { duration: 0.3 },
    });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: 100,
      onComplete: () => {
        index.value = Math.min(i, selectedWorksProps.length - 1);
      },
    }).to('#index', {
      yPercent: 0,
      ease: 'power1.inOut',
    });

    return tl;
  };

  // Reusable function to handle backward scroll animation
  const createBackwardTimeline = (index: any, i: any) => {
    const tl = gsap.timeline({ defaults: { duration: 0.3 } });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: -100,
      onComplete: () => {
        index.value = Math.max(i, 0);
      },
    }).to('#index', {
      yPercent: 0,
      duration: 0.3,
      ease: 'power1.inOut',
    });

    return tl;
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const video = entry.target as HTMLVideoElement;
      if (entry.isIntersecting) {
        video.play();
        video.classList.remove('blur');
      }
    });
  };

  const stopAllVideos = () => {
    videoRefs.value?.map((video: HTMLVideoElement) => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0; // Reset video to the start
      }
    });
  };
  onBeforeMount(() => {
    selectedWorks.value = textSplitterIntoChar('Selected Works / ', true);
  });

  onMounted(() => {
    stopAllVideos();

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.75, // Trigger when 75% of the video is visible
    });

    // Observe each video element
    videoRefs.value?.forEach((video) => {
      observer.observe(video);
    });

    animateSplitText(
      '#selectedWorks .letters',
      '#selected-works-text',
      0.7,
      0.01,
      0,
    );

    // Apply GSAP animations to each div
    if (!isSmallScreen.value)
      gsap.utils.toArray('.work-card').forEach((div: any, i: any) => {
        gsap.timeline({ defaults: { duration: 0.7 } }).to(div, {
          scrollTrigger: {
            trigger: div,
            // start: 'top 40%',
            start: 'top 25%',
            // end: 'bottom 40%',
            end: 'bottom 25%',
            scrub: 0.01,
            // markers: true,
            onLeaveBack: () => {
              // Backward scroll animation
              if (index.value !== 0) {
                gsap.to('#index', {
                  yPercent: 100,
                  duration: 0.3,
                  ease: 'power4.inOut',
                  onComplete: () => {
                    createBackwardTimeline(index, i - 1);
                  },
                });
              }
            },
          },
          ease: 'power1.inOut',
          onComplete: () => {
            // Forward scroll animation
            if (index.value !== selectedWorksProps.length - 1) {
              gsap.to('#index', {
                yPercent: -100,
                duration: 0.3,
                ease: 'power4.inOut',
                onComplete: () => {
                  createForwardTimeline(index, i + 1, selectedWorksProps);
                },
              });
            }
          },
        });
      });
  });
</script>
